<div class="column drawer-dialog">
	<div class="drawer-dialog-header">
		@if (!isEditMode) {
			<h2 i18n="@@assignDeviceControl-GrantAccess" data-automation-id="assignDeviceControl-GrantAccess">
				Grant Access
			</h2>
		}
		@if (isEditMode) {
			<h2 i18n="@@assignDeviceControl-EditAccess" data-automation-id="assignDeviceControl-EditAccess">Edit Access</h2>
		}
		<button mat-icon-button>
			<mat-icon (click)="onCloseClicked()" data-automation-id="assign-device-controls-close-button">close</mat-icon>
		</button>
	</div>

	<app-not-authorized-notice has="deviceManagement"></app-not-authorized-notice>

	<mat-dialog-content class="drawer-dialog-content" [appHasSystemCapability] has="deviceManagement">
		<!-- Access Group Selection Step - Only visible in edit mode when multiple access groups exist -->
		@if (showAccessGroupSelectionStep) {
			<div class="access-group-selection-step">
				<div class="access-group-selection-header">
					<h3
						class="font-weight-400 font-size-16 inheritance-bubbles-text inheritance-bubble-header-text"
						i18n="@@assignDeviceControl-accessGroupSelectionHeader"
					>
						The selected companies belong to different access groups. Please choose one access group to edit - you'll
						need to modify the other groups separately.
					</h3>
				</div>
				<!-- Selectable inheritance bubbles -->
				<div class="access-groups-container">
					<mat-grid-list cols="1" [rowHeight]="gridRowHeight" gutterSize="5px">
						@for (accessGroup of accessGroups; track accessGroup.control.id || accessGroup.control.name) {
							<mat-grid-tile
								(click)="onAccessGroupSelected(accessGroup)"
								[attr.data-automation-id]="'access-group-' + (accessGroup.control.id || 'unknown') + '-tile'"
							>
								<div
									class="column access-group-container"
									[class.selected]="selectedAccessGroup?.control?.id === accessGroup.control.id"
									[class.notSelected]="selectedAccessGroup?.control?.id !== accessGroup.control.id"
								>
									<div class="column">
										<div class="access-group-header-container">
											<p class="access-group-control-header text-overflow-ellipsis">
												{{ formatControlName(accessGroup.control) }}
											</p>
											<div class="hide-on-mobile" style="padding-left: 8px">
												<ng-container
													*ngTemplateOutlet="iconRowTemplate; context: { deviceAccess: accessGroup }"
												></ng-container>
											</div>
										</div>
										<div class="row">
											<div class="column-double">
												<p class="access-group-control-day-time inheritance-bubbles-text">
													{{ accessGroup.control | deviceControlDays }}
												</p>
											</div>
											<div class="duration-icons-row">
												<p class="access-group-control-duration inheritance-bubbles-text">
													{{ accessGroup.assignments | deviceAccessControlDuration }}
												</p>
												<div class="show-on-mobile">
													<ng-container
														*ngTemplateOutlet="iconRowTemplate; context: { deviceAccess: accessGroup }"
													></ng-container>
												</div>
											</div>
										</div>
									</div>
								</div>
							</mat-grid-tile>
						}
					</mat-grid-list>
				</div>
				<mat-divider class="mat-divider-spacing"></mat-divider>
				<!-- Affected companies/users list -->
				@if (selectedAccessGroup) {
					<div class="affected-entities-container">
						@if (assignmentType === 'company') {
							<h4 class="affected-entities-header">
								<span i18n="@@assignDeviceControl-affectedCompaniesHeader-thisChangeWillAffect"
									>This change will affect</span
								><span class="blue-color">{{ ' ' + affectedAssignments.length + ' ' }}</span
								><span i18n="@@assignDeviceControl-affectedCompaniesHeader-companies">companies</span>:
							</h4>
						}
						@if (assignmentType === 'user') {
							<h4 class="affected-entities-header">
								<span i18n="@@assignDeviceControl-affectedUsersHeader-thisChangeWillAffect"
									>This change will affect</span
								><span class="blue-color">{{ ' ' + affectedAssignments.length + ' ' }}</span
								><span i18n="@@assignDeviceControl-affectedUsersHeader-users">users</span>:
							</h4>
						}
						<ul class="affected-entities-list">
							@for (entity of affectedAssignments; track entity.childEntityName) {
								<li class="affected-entity-item">
									{{ entity.childEntityName }}
								</li>
							}
						</ul>
					</div>
				}
			</div>
		}

		<!-- Normal form content - hidden when showing access group selection -->
		@if (!showAccessGroupSelectionStep) {
			<div>
				<app-loading-indicator
					[keys]="[
						apiLoadingKeys.getDeviceControls,
						apiLoadingKeys.getControlAssignmentOptions,
						apiLoadingKeys.getUserProfile,
						apiLoadingKeys.getDeviceGroupOptions,
						apiLoadingKeys.getInheritedDeviceGroupControlById,
						apiLoadingKeys.getFeatureFlagByName,
						apiLoadingKeys.getCompanyRelationshipsByCompanyId,
						apiLoadingKeys.createDeviceControlsAndControlAssignments,
					]"
				>
					@if (this.fetchingDeviceControlSummaries) {
						<div>
							<span i18n="@@assignDeviceControl-fetchingControls">Fetching controls...</span>
						</div>
					}
					@if (!this.fetchingDeviceControlSummaries && this.resultingDeviceAccess?.length === 0) {
						<div>
							<span i18n="@@assignDeviceControl-noControlsFound">No device access controls found.</span>
						</div>
					}
					@if (this.resultingDeviceAccess?.length) {
						<div>
							@if (!isEditMode) {
								<h3
									class="font-weight-400 font-size-14 inheritance-bubbles-text inheritance-bubble-header-text"
									i18n="@@assignDeviceControl-GrantAccessSelection"
								>
									Get started by clicking on an access you've been granted
								</h3>
							}
							@if (isEditMode) {
								<h3
									class="font-weight-400 font-size-14 inheritance-bubbles-text inheritance-bubble-header-text"
									i18n="@@assignDeviceControl-EditAccessSelection"
								>
									Editing Limited to This Access:
								</h3>
							}
							<mat-grid-list cols="1" [rowHeight]="gridRowHeight" gutterSize="5px">
								@for (
									deviceAccess of resultingDeviceAccess;
									track deviceAccess.control.id || deviceAccess.control.name
								) {
									<mat-grid-tile
										id="inherited_control_mat_grid"
										(click)="onSelectedInheritorChangedEvent(deviceAccess)"
										[attr.data-automation-id]="'resulting-device-access-' + deviceAccess.control.id + '-tile'"
									>
										<div
											class="column resulting-device-access-container"
											[class.selected]="deviceAccess.control.name === 'selected'"
											[class.notSelected]="deviceAccess.control.name !== 'selected'"
										>
											<div class="column">
												<div class="device-control-header-container">
													<p class="resulting-device-access-control-header text-overflow-ellipsis">
														{{ formatControlName(deviceAccess.control) }}
													</p>
													<div class="hide-on-mobile" style="padding-left: 8px">
														<ng-container
															*ngTemplateOutlet="iconRowTemplate; context: { deviceAccess: deviceAccess }"
														></ng-container>
													</div>
												</div>
												<div class="row">
													<div class="column-double">
														<p class="resulting-device-access-control-day-time inheritance-bubbles-text">
															{{ deviceAccess.control | deviceControlDays }}
														</p>
													</div>
													<div class="duration-icons-row">
														<p class="resulting-device-access-control-duration inheritance-bubbles-text">
															{{ deviceAccess.assignments | deviceAccessControlDuration }}
														</p>
														<div class="show-on-mobile">
															<ng-container
																*ngTemplateOutlet="iconRowTemplate; context: { deviceAccess: deviceAccess }"
															></ng-container>
														</div>
													</div>
												</div>
											</div>
										</div>
									</mat-grid-tile>
								}
							</mat-grid-list>
							<mat-divider class="mat-divider-spacing"></mat-divider>
						</div>
					}
					<!-- added this so the user doesn't see days of the week, time or DC until the inherited device controls are fetched -->
					@if (this.resultingDeviceAccess?.length || this.hasOwnedDevices) {
						<app-device-control-details-common
							[edits]="edits.control"
							[assignmentType]="assignmentType"
							[deviceAssignmentEdits]="deviceAssignmentEdits"
							[isHaspCodeGenFeatureEnabled]="isHaspCodeGenFeatureEnabled"
							[isPDQOnly]="hasOnlyPDQDevices"
							[hasAtLeastOneAJAXDevice]="hasAtLeastOneAJAXDevice"
							[isSentriVaultDevice]="hasSentriVaultDevices"
							[isSpecialGrantAccessOnly]="hasOnlySpecialGrantAccessDevices"
							[isGen3WallMountOnly]="hasOnlyGen3WallMountDevices"
							[minStartTime]="minStartTime"
							[maxEndTime]="maxEndTime"
						>
						</app-device-control-details-common>
					}
					@if (this.hasOwnedDevices) {
						@if (!hideCompanyUserSelection) {
							@if (assignmentType === 'user') {
								<div class="flex-row-space-between">
									<h3 class="drawer-dialog-content-header" i18n="@@assignDeviceControl-userSelection">
										User Selection
									</h3>
									<span class="asterisk-text">*</span>
								</div>
								<app-multi-select-typeahead
									class="assignment-user-selection-typeahead"
									data-automation-id="assignment-user-selection-typeahead"
									[allDropdownOptions]="controlAssignment"
									[filterOnField]="'displayName'"
									[placeholderText]="localizedText.user"
									[showSelectionsInInputField]="false"
									[showSelectedItemSubOption]="false"
									[selectedItemIconName]="'sl-add-user-21'"
									[isUserSelection]="true"
									[autoFocus]="false"
									[isRequired]="true"
									(selectedOptionsChangedEvent)="onSelectedUsersChangedEvent($event)"
								>
								</app-multi-select-typeahead>
							}
							@if (assignmentType === 'company') {
								<div class="flex-row-space-between">
									<h3 class="drawer-dialog-content-header" i18n="@@assignDeviceControl-companySelection">
										Company Selection
									</h3>
									<span class="asterisk-text">*</span>
								</div>
								<app-multi-select-typeahead
									data-automation-id="company-selection-typeahead"
									[allDropdownOptions]="controlAssignment"
									[filterOnField]="'displayName'"
									[placeholderText]="localizedText.company"
									[showSelectionsInInputField]="false"
									[showSelectedItemSubOption]="true"
									[subOptionFieldName]="'allowSubAuthorization'"
									[subOptionText]="localizedText.companySuboptionText"
									[subOptionTooltip]="'Enable this company to grant device access to other companies.'"
									[selectedItemIconName]="'sl-nav-work-17'"
									[autoFocus]="false"
									[isRequired]="true"
									(selectedOptionsChangedEvent)="onSelectedCompaniesChangedEvent($event)"
								>
								</app-multi-select-typeahead>
							}
						}
						@if (!hideDeviceGroupSelection) {
							@if (!hideCompanyUserSelection) {
								<mat-divider class="mat-divider-spacing"></mat-divider>
							}
							<h3 class="drawer-dialog-content-header" i18n="@@assignDeviceControl-deviceGroupSelection">
								Device Group Selection
							</h3>
							<app-multi-select-typeahead
								data-automation-id="device-group-selection-typeahead"
								class="unit-test-device-group-selection"
								[allDropdownOptions]="deviceGroupOptions"
								[filterOnField]="'name'"
								[initiallySelectedIdField]="'id'"
								[initiallySelectedItemIds]="initiallySelectedDeviceGroupIds"
								[placeholderText]="localizedText.deviceGroup"
								[showSelectionsInInputField]="false"
								[showSelectedItemSubOption]="false"
								[selectedItemIconName]="'sl-lock'"
								[showSelectedItemMessage]="false"
								[showMessageFieldName]="'allAllowSubAuthorization'"
								[showMessageText]="localizedText.deviceGroupMessage"
								[autoFocus]="false"
								(selectedOptionsChangedEvent)="onSelectedDeviceGroupsChangedEvent($event)"
							>
							</app-multi-select-typeahead>
						}
						@if (!hideCompanyUserSelection || !hideDeviceGroupSelection) {
							<mat-divider class="mat-divider-spacing"></mat-divider>
						}
						<div class="datepicker-selection">
							<h3 class="drawer-dialog-content-header" i18n="@@assignDeviceControl-durationHeader">Duration</h3>
							<div class="row spacing-24">
								<div class="date-container">
									<div class="column start-date-container">
										<div class="flex-row-space-between">
											<div class="label-text-formatting" i18n="@@assignDeviceControl-startDateHeader">Start Date</div>
											<span class="asterisk-text">*</span>
										</div>
										<mat-form-field appearance="outline" subscriptSizing="dynamic" floatLabel="auto">
											<input
												required
												matInput
												[matDatepicker]="startPicker"
												[formControl]="startDateTimeControl"
												[placeholder]="'MM/DD/YY' | accessDatePlaceholderPipe"
												(dateChange)="onStartDateChanged($event)"
												[min]="minStartDate"
												[max]="maxStartDate"
												data-automation-id="start-date-input"
											/>
											<mat-datepicker-toggle
												matSuffix
												class="date-picker-primary-blue"
												[for]="startPicker"
												data-automation-id="start-date-toggle"
											>
												<mat-icon matDatepickerToggleIcon class="calendar-icon" svgIcon="sl-nav-calendar"></mat-icon>
											</mat-datepicker-toggle>
											<mat-datepicker #startPicker data-automation-id="start-date-datepicker"></mat-datepicker>
										</mat-form-field>
									</div>
									<div i18n="@@assignDeviceControl-to" class="to-text">To</div>
									<div class="column end-date-container">
										<div class="label-text-formatting" i18n="@@assignDeviceControl-endDateHeader">End Date</div>
										<mat-form-field subscriptSizing="dynamic" appearance="outline">
											<input
												matInput
												[matDatepicker]="endPicker"
												[placeholder]="'MM/DD/YY' | accessDatePlaceholderPipe"
												[formControl]="endDateTimeControl"
												i18n-placeholder="@@assignDeviceControl-ChooseEndDate"
												(dateChange)="onEndDateChanged($event)"
												[min]="minEndDate"
												[max]="maxEndDate"
												data-automation-id="end-date-input"
											/>
											<mat-datepicker-toggle
												matSuffix
												class="date-picker-primary-blue"
												[for]="endPicker"
												data-automation-id="end-date-toggle"
											>
												<mat-icon matDatepickerToggleIcon class="calendar-icon" svgIcon="sl-nav-calendar"></mat-icon>
											</mat-datepicker-toggle>
											<mat-datepicker #endPicker data-automation-id="end-date-datepicker"></mat-datepicker>
										</mat-form-field>
									</div>
								</div>
							</div>
							<!-- Sub-Authorization Section - Only visible when editing -->
							@if (isEditMode && assignmentType === 'company') {
								<div>
									<mat-divider class="mat-divider-spacing"></mat-divider>
									<div class="subauthorization-section">
										<div class="flex-row-space-between">
											<div class="drawer-dialog-content-header" i18n="@@assignDeviceControl-subauthorizationHeader">
												Sub-Authorization
											</div>
											<span class="asterisk-text">*</span>
										</div>
										<div class="subauthorization-content">
											<div class="subauthorization-row">
												<div class="subauthorization-label-container">
													<span class="subauthorization-label" i18n="@@assignDeviceControl-allowSubAuthorization">
														Allow Sub-Authorization
													</span>
													<mat-icon
														class="tooltip-icon"
														svgIcon="sl-info-square"
														matTooltip="Enable this company to grant device access to other companies."
														i18n-matTooltip="@@assignDeviceControl-subauthorizationTooltip"
													></mat-icon>
												</div>
												<div class="subauthorization-controls">
													<mat-form-field appearance="outline" subscriptSizing="dynamic" class="select-border">
														<mat-select
															[formControl]="subauthorizationControl"
															data-automation-id="subauthorization-dropdown"
															placeholder="Select"
														>
															@for (option of subauthorizationOptions; track option) {
																<mat-option [value]="option.value" [attr.i18n]="option.i18n">
																	{{ option.label }}
																</mat-option>
															}
														</mat-select>
													</mat-form-field>
												</div>
											</div>
										</div>
									</div>
								</div>
							}
							<!-- Descendant Companies Section - Only visible when editing -->
							@if (isEditMode && assignmentType === 'company') {
								<div>
									@if (descendantCompanies.length > 0) {
										<div class="descendant-companies-container">
											<h4
												id="descendantCompaniesHeader"
												class="descendant-companies-header"
												i18n="@@assignDeviceControl-descendantCompaniesHeader"
											>
												The following companies may be impacted by your access control changes:
											</h4>
											<ul id="descendantCompaniesList" class="descendant-companies-list">
												@for (company of descendantCompanies; track company.id) {
													<li class="descendant-company-item">
														{{ company.childCompanyName }}
													</li>
												}
											</ul>
										</div>
									}
									@if (
										(data.controlAssignments && data.controlAssignments.length > 1) ||
										(this.descendantCompanies && this.descendantCompanies.length > 0)
									) {
										<div class="select-multiple-companies-warning-container">
											<span class="alert-circle-icon"><mat-icon svgIcon="sl-alert-circle"></mat-icon></span>
											<div>
												<span><b i18n="@@assignDeviceControl-important">Important:</b></span> {{ ' ' }}
												<span
													id="modifyDownstreamWarning"
													i18n="@@assignDeviceControl-modifyAccessControlsForMultipleCompaniesWarning"
													>You are about to modify access controls for multiple companies. Removing controls will
													immediately affect all downstream companies, while adding controls will not automatically
													apply to them.</span
												>
											</div>
										</div>
									}
								</div>
							}
						</div>
					} @else if (isEditMode || this.resultingDeviceAccess?.length) {
						@if (!hideCompanyUserSelection) {
							@if (assignmentType === 'user') {
								<div class="flex-row-space-between">
									<h3 class="drawer-dialog-content-header" i18n="@@assignDeviceControl-userSelection">
										User Selection
									</h3>
									<span class="asterisk-text">*</span>
								</div>
								<app-multi-select-typeahead
									class="assignment-user-selection-typeahead"
									data-automation-id="assignment-user-selection-typeahead"
									[allDropdownOptions]="controlAssignment"
									[filterOnField]="'displayName'"
									[placeholderText]="localizedText.user"
									[showSelectionsInInputField]="false"
									[showSelectedItemSubOption]="false"
									[selectedItemIconName]="'sl-add-user-21'"
									[isUserSelection]="true"
									[autoFocus]="false"
									[isRequired]="true"
									(selectedOptionsChangedEvent)="onSelectedUsersChangedEvent($event)"
								>
								</app-multi-select-typeahead>
							}
							@if (assignmentType === 'company') {
								<div class="flex-row-space-between">
									<h3 class="drawer-dialog-content-header" i18n="@@assignDeviceControl-companySelection">
										Company Selection
									</h3>
									<span class="asterisk-text">*</span>
								</div>
								<app-multi-select-typeahead
									data-automation-id="assignment-company-selection-typeahead"
									[allDropdownOptions]="controlAssignment"
									[filterOnField]="'displayName'"
									[placeholderText]="localizedText.company"
									[showSelectionsInInputField]="false"
									[showSelectedItemSubOption]="true"
									[subOptionFieldName]="'allowSubAuthorization'"
									[subOptionText]="localizedText.companySuboptionText"
									[subOptionTooltip]="localizedText.companySuboptionTooltip"
									[showSelectedItemSubOption2]="this.showCodeGenerationOption"
									[subOptionFieldName2]="'allowCodeGeneration'"
									[subOptionText2]="localizedText.companySuboptionText2"
									[selectedItemIconName]="'sl-nav-work-17'"
									[autoFocus]="false"
									[isRequired]="true"
									(selectedOptionsChangedEvent)="onSelectedCompaniesChangedEvent($event)"
								>
								</app-multi-select-typeahead>
							}
							@if (!hideCompanyUserSelection) {
								<mat-divider class="mat-divider-spacing"></mat-divider>
							}
						}
						@if (!hideDeviceGroupSelection) {
							<div class="flex-row-space-between">
								<h3 class="drawer-dialog-content-header" i18n="@@assignDeviceControl-deviceGroupSelection_hasNoDevices">
									Device Group selection
								</h3>
								<span class="asterisk-text">*</span>
							</div>
							<app-multi-select-typeahead
								data-automation-id="assignment-device-group-selection-typeahead"
								class="unit-test-device-group-selection"
								[allDropdownOptions]="deviceGroupOptions"
								[filterOnField]="'name'"
								[initiallySelectedIdField]="'id'"
								[initiallySelectedItemIds]="initiallySelectedDeviceGroupIds"
								[placeholderText]="localizedText.deviceGroup"
								[showSelectionsInInputField]="false"
								[showSelectedItemSubOption]="false"
								[selectedItemIconName]="'sl-lock'"
								[showSelectedItemMessage]="false"
								[showMessageFieldName]="'allAllowSubAuthorization'"
								[showMessageText]="localizedText.deviceGroupMessage"
								[autoFocus]="false"
								[isRequired]="true"
								(selectedOptionsChangedEvent)="onSelectedDeviceGroupsChangedEvent($event)"
							>
							</app-multi-select-typeahead>
							@if (!hideCompanyUserSelection || !hideDeviceGroupSelection) {
								<mat-divider class="mat-divider-spacing"></mat-divider>
							}
						}
						<div class="datepicker-selection">
							<h3 class="drawer-dialog-content-header" i18n="@@assignDeviceControl-durationHeader">Duration</h3>
							<div class="row spacing-24">
								<div class="date-container">
									<div class="column start-date-container">
										<div class="flex-row-space-between">
											<div class="label-text-formatting" i18n="@@assignDeviceControl-startDateHeader">Start Date</div>
											<span class="asterisk-text">*</span>
										</div>
										<mat-form-field appearance="outline" subscriptSizing="dynamic" floatLabel="auto">
											<input
												required
												matInput
												[matDatepicker]="startPicker"
												[formControl]="startDateTimeControl"
												[placeholder]="'MM/DD/YY' | accessDatePlaceholderPipe"
												(dateChange)="onStartDateChanged($event)"
												[min]="minStartDate"
												[max]="maxStartDate"
												data-automation-id="start-date-input"
											/>
											<mat-datepicker-toggle
												matSuffix
												class="date-picker-primary-blue"
												[for]="startPicker"
												data-automation-id="assignment-start-date-toggle"
											>
												<mat-icon matDatepickerToggleIcon class="calendar-icon" svgIcon="sl-nav-calendar"></mat-icon>
											</mat-datepicker-toggle>
											<mat-datepicker
												#startPicker
												data-automation-id="assignment-start-date-datepicker"
											></mat-datepicker>
										</mat-form-field>
									</div>
									<div i18n="@@assignDeviceControl-to" class="to-text">To</div>
									<div class="column end-date-container">
										<div class="label-text-formatting" i18n="@@assignDeviceControl-endDateHeader">End Date</div>
										<mat-form-field subscriptSizing="dynamic" appearance="outline">
											<input
												matInput
												[matDatepicker]="endPicker"
												[placeholder]="'MM/DD/YY' | accessDatePlaceholderPipe"
												[formControl]="endDateTimeControl"
												i18n-placeholder="@@assignDeviceControl-ChooseEndDate"
												(dateChange)="onEndDateChanged($event)"
												[min]="minEndDate"
												[max]="maxEndDate"
												data-automation-id="end-date-input"
											/>
											<mat-datepicker-toggle
												matSuffix
												class="date-picker-primary-blue"
												[for]="endPicker"
												data-automation-id="end-date-toggle"
											>
												<mat-icon matDatepickerToggleIcon class="calendar-icon" svgIcon="sl-nav-calendar"></mat-icon>
											</mat-datepicker-toggle>
											<mat-datepicker #endPicker data-automation-id="end-date-datepicker"></mat-datepicker>
										</mat-form-field>
									</div>
								</div>
							</div>
							<!-- Sub-Authorization Section - Only visible when editing -->
							@if (isEditMode && assignmentType === 'company') {
								<div>
									<mat-divider class="mat-divider-spacing"></mat-divider>
									<div class="subauthorization-section">
										<div class="flex-row-space-between">
											<div class="drawer-dialog-content-header" i18n="@@assignDeviceControl-subauthorizationHeader">
												Sub-Authorization
											</div>
											<span class="asterisk-text">*</span>
										</div>
										<div class="subauthorization-content">
											<div class="subauthorization-row">
												<div class="subauthorization-label-container">
													<span class="subauthorization-label" i18n="@@assignDeviceControl-allowSubAuthorization">
														Allow Sub-Authorization
													</span>
													<mat-icon
														class="tooltip-icon"
														svgIcon="sl-info-square"
														matTooltip="Enable this company to grant device access to other companies."
														i18n-matTooltip="@@assignDeviceControl-subauthorizationTooltip"
													></mat-icon>
												</div>
												<div class="subauthorization-controls">
													<mat-form-field appearance="outline" subscriptSizing="dynamic" class="select-border">
														<mat-select
															[formControl]="subauthorizationControl"
															data-automation-id="subauthorization-dropdown"
															placeholder="Select"
														>
															@for (option of subauthorizationOptions; track option) {
																<mat-option [value]="option.value" [attr.i18n]="option.i18n">
																	{{ option.label }}
																</mat-option>
															}
														</mat-select>
													</mat-form-field>
												</div>
											</div>
										</div>
									</div>
								</div>
							}
							<!-- Descendant Companies Section - Only visible when editing -->
							@if (isEditMode && assignmentType === 'company') {
								<div>
									@if (descendantCompanies.length > 0) {
										<div class="descendant-companies-container">
											<h4
												id="descendantCompaniesHeader"
												class="descendant-companies-header"
												i18n="@@assignDeviceControl-descendantCompaniesHeader"
											>
												The following companies may be impacted by your access control changes:
											</h4>
											<ul id="descendantCompaniesList" class="descendant-companies-list">
												@for (company of descendantCompanies; track company.id) {
													<li class="descendant-company-item">
														{{ company.childCompanyName }}
													</li>
												}
											</ul>
										</div>
									}
									@if (
										(data.controlAssignments && data.controlAssignments.length > 1) ||
										(this.descendantCompanies && this.descendantCompanies.length > 0)
									) {
										<div class="select-multiple-companies-warning-container">
											<span class="alert-circle-icon"><mat-icon svgIcon="sl-alert-circle"></mat-icon></span>
											<div>
												<span><b i18n="@@assignDeviceControl-important">Important:</b></span> {{ ' ' }}
												<span
													id="modifyDownstreamWarning"
													i18n="@@assignDeviceControl-modifyAccessControlsForMultipleCompaniesWarning"
													>You are about to modify access controls for multiple companies. Removing controls will
													immediately affect all downstream companies, while adding controls will not automatically
													apply to them.</span
												>
											</div>
										</div>
									}
								</div>
							}
						</div>
					}
				</app-loading-indicator>
			</div>
		}
	</mat-dialog-content>

	<mat-dialog-actions class="margin-bottom-none" [appHasSystemCapability] has="deviceManagement">
		<div class="row row-end">
			<div class="bottom-border"></div>

			<!-- Access Group Selection Step Actions -->
			@if (showAccessGroupSelectionStep) {
				<div>
					<button
						class="secondary-small-button"
						(click)="onCancelAccessGroupSelection()"
						i18n="@@assignDeviceControl-cancel"
						data-automation-id="access-group-selection-cancel-button"
					>
						Cancel
					</button>
					<button
						class="primary-small-button primary-small-button-wider margin-right-32"
						(click)="onNextStepClicked()"
						[disabled]="!selectedAccessGroup"
						data-automation-id="access-group-selection-next-button"
					>
						Next Step
					</button>
				</div>
			}

			<!-- Normal Form Actions -->
			@if (!showAccessGroupSelectionStep) {
				<div>
					@if (isEditMode && hadMultipleAccessGroups) {
						<button
							class="secondary-small-button"
							(click)="onBackToAccessGroupSelection()"
							[disabled]="isSaving"
							i18n="@@assignDeviceControl-back"
							data-automation-id="assign-device-controls-back-button"
						>
							Back
						</button>
					}
					@if (!isEditMode || !hadMultipleAccessGroups) {
						<button
							class="secondary-small-button"
							(click)="onCloseClicked()"
							[disabled]="isSaving"
							i18n="@@assignDeviceControl-closeWindow"
							data-automation-id="assign-device-controls-close-button"
						>
							Close
						</button>
					}
					@if (this.hasOwnedDevices && !isEditMode) {
						<button
							class="primary-small-button primary-small-button-wider margin-right-32"
							(click)="saveAssignments()"
							[disabled]="!canSave() || isSaving"
							data-automation-id="assign-device-controls-grant-access-button"
						>
							@if (isSaving) {
								<mat-spinner [diameter]="isMobile ? 25 : 35" class="button-spinner" color="accent"></mat-spinner>
							}
							@if (!isSaving) {
								<span i18n="@@assignDeviceControl-hasOwned-saveAssignments">Grant Access</span>
							}
						</button>
					}
					@if (this.hasOwnedDevices && isEditMode) {
						<button
							class="primary-small-button primary-small-button-wider margin-right-32 button-with-spinner"
							(click)="saveAssignments()"
							[disabled]="!canSave() || isSaving"
							data-automation-id="assign-device-controls-update-access-button"
						>
							@if (isSaving) {
								<mat-spinner [diameter]="isMobile ? 25 : 35" class="button-spinner" color="accent"></mat-spinner>
							}
							@if (!isSaving) {
								<span i18n="@@assignDeviceControl-hasOwned-updateAssignments">Update Access</span>
							}
						</button>
					}
					@if (!this.hasOwnedDevices && !isEditMode && this.resultingDeviceAccess?.length) {
						<button
							class="primary-small-button primary-small-button-wider margin-right-32"
							(click)="saveAssignments()"
							[disabled]="!canSubAuthSave() || isSaving"
							data-automation-id="assign-device-controls-grant-access-button"
						>
							@if (isSaving) {
								<mat-spinner [diameter]="isMobile ? 25 : 35" class="button-spinner" color="accent"></mat-spinner>
							}
							@if (!isSaving) {
								<span i18n="@@assignDeviceControl-notHasOwned-saveAssignments">Grant Access</span>
							}
						</button>
					}
					@if (!this.hasOwnedDevices && isEditMode) {
						<button
							class="primary-small-button primary-small-button-wider margin-right-32"
							(click)="saveAssignments()"
							[disabled]="!canSubAuthSave() || isSaving"
							data-automation-id="assign-device-controls-update-access-button"
						>
							@if (isSaving) {
								<mat-spinner [diameter]="isMobile ? 25 : 35" class="button-spinner" color="accent"></mat-spinner>
							}
							@if (!isSaving) {
								<span i18n="@@assignDeviceControl-notHasOwned-updateAssignments">Update Access</span>
							}
						</button>
					}
				</div>
			}

			<div class="actions-spacer"></div>
		</div>
	</mat-dialog-actions>
</div>

<!-- Icon row template to avoid duplication -->
<ng-template #iconRowTemplate let-deviceAccess="deviceAccess">
	<div>
		@if (
			(deviceAccess.control.permissions.canOpenKeyDoor.value ||
				deviceAccess.control.permissions.canReleaseHasp.value) &&
			!hasOnlySpecialGrantAccessDevices
		) {
			<mat-icon svgIcon="sl-open-key-door" class="capabilities-icon" matTooltip="{{ deviceAccessTooltip }}"></mat-icon>
		}
		@if (deviceAccess.control.permissions.canInstallDevice.value && !hasOnlySpecialGrantAccessDevices) {
			<mat-icon
				svgIcon="sl-install-device"
				class="capabilities-icon"
				matTooltip="{{ installDeviceTooltip }}"
			></mat-icon>
		}
		@if (deviceAccess.control.permissions.canRemoveDevice.value && !hasOnlySpecialGrantAccessDevices) {
			<mat-icon svgIcon="sl-remove-device" class="capabilities-icon" matTooltip="{{ removeDeviceTooltip }}"></mat-icon>
		}
		@if ((deviceAccess.assignments[0].allowCodeGeneration ?? false) && isHaspCodeGenFeatureEnabled) {
			<mat-icon
				svgIcon="sl-generate-access-code"
				class="capabilities-icon"
				matTooltip="{{ accessCodeTooltip }}"
			></mat-icon>
		}
		@if (
			(deviceAccess.assignments[0].allowHaspCodeGeneration ?? false) &&
			isHaspCodeGenFeatureEnabled &&
			!hasOnlySpecialGrantAccessDevices &&
			!hasOnlyGen3WallMountDevices
		) {
			<mat-icon svgIcon="sl-generate-hasp-code" class="capabilities-icon" matTooltip="{{ haspCodeTooltip }}"></mat-icon>
		}
		@if (deviceAccess.control.permissions.canArmDisarmAjaxAlarm.value) {
			<mat-icon
				svgIcon="sl-shield-checkmark"
				class="capabilities-icon"
				matTooltip="{{ armDisarmAlarmTooltip }}"
			></mat-icon>
		}
		@if (deviceAccess.control.permissions.canOverrideAjaxAlarm.value) {
			<mat-icon
				svgIcon="sl-bell-crossed-out"
				class="capabilities-icon"
				matTooltip="{{ alarmOverrideTooltip }}"
			></mat-icon>
		}
	</div>
</ng-template>
